/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Images, Section, SectionItem } from "@packages/sdk";
import { forwardRef, type JSX } from "react";

import { spacing } from "../../../../../../global/emotion";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import type { AuthorProps } from "../../../_core/Author/Author.emotion";
import { Author } from "../../../_core/Author/Author.emotion";
import { IconButton } from "../../../_core/IconButton/IconButton.emotion";
import { MoreIcon } from "../../../icons/Icon/NavigationIcons";
import type { CampaignLargeProps } from "../CampaignLarge/CampaignLarge.emotion";
import { CampaignLarge } from "../CampaignLarge/CampaignLarge.emotion";
import { CampaignModalDropdownMenu } from "../CampaignModalDropdownMenu/CampaignModalDropdownMenu.emotion";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
};

export type CampaignLargeWithAuthorProps = WithAsChild<
  HallowElementProps<"div">
> & {
  authorProps?: AuthorProps;
  campaignId: number;
  campaignName?: string;
  campaignLargeProps?: Partial<CampaignLargeProps>;
  color: CSSColorStrict;
  description: string;
  illustrationImgSrc?: string;
  imgSrc?: Images;
  item?: SectionItem;
  joined?: boolean;
  hasEnded?: boolean;
  section?: Partial<Section>;
  tags: string[];
  title: string;
};

export const CampaignLargeWithAuthor = forwardRef<
  any,
  CampaignLargeWithAuthorProps
>(
  (
    {
      asChild = false,
      authorProps,
      campaignId,
      campaignName,
      campaignLargeProps,
      color,
      children,
      description,
      illustrationImgSrc,
      imgSrc,
      item,
      joined,
      hasEnded,
      section,
      tags,
      title,
      ...props
    },
    ref,
  ) => {
    const analytics = useAnalytics();

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <Author
          key="author"
          rightActions={[
            <CampaignModalDropdownMenu
              key={`${campaignId}_dropdown_menu`}
              campaignId={campaignId}
              campaignName={campaignName}
              campaignSmallProps={{
                color,
                description,
                illustrationImgSrc,
                imgSrc,
                metadata: tags ? tags[0] : null,
                title,
              }}
              dropdownMenuProps={{
                align: "end" as const,
                modal: false,
              }}
              joined={joined}
              hasEnded={hasEnded}
              trigger={
                <IconButton
                  icon={<MoreIcon />}
                  onPointerDown={() =>
                    // TODO: mobile calls this track event from parent
                    analytics.track({
                      event: HallowAnalyticsEvent.TappedSectionItemMoreMenu,
                      properties: {
                        // TODO: notes say page_id = -1 for campaign_list 'Viewed Screen' event, but requestPagesTrusted returns 83
                        // does home have a different page_id? JP: yes, and we have different homes (day/night)
                        // when we refactor this to call from parent, add proper page ids
                        page_id: -1,
                        section_id: section?.id,
                        section_item_id: item?.item_id ?? null,
                        section_title: section?.title ?? null,
                        section_type: section?.type ?? null,
                        index: section?.items.indexOf(item) ?? null,
                        item_type: item?.type ?? null,
                        section_cohort_id:
                          section?.cohorts?.map((c) => c.id) ?? null,
                        section_cohort:
                          section?.cohorts?.map((c) => c.name) ?? null,
                      },
                    })
                  }
                  variant="transparentNeutral"
                />
              }
            />,
          ]}
          {...authorProps}
        />,
        <CampaignLarge
          color={color}
          description={description}
          illustrationImgSrc={illustrationImgSrc}
          imgSrc={imgSrc}
          joined={joined}
          key="campaign"
          tags={tags}
          title={title}
          {...campaignLargeProps}
        />,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement ref={ref} css={determinedElementStyle} {...props}>
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

CampaignLargeWithAuthor.displayName = "CampaignLargeWithAuthor";
